import { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';

interface SearchInputDebouncedProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceDelay?: number;
  className?: string;
  searchBy?: string;
  disabled?: boolean;
  loading?: boolean;
}

export default function SearchInputDebounced({
  value: externalValue = '',
  onChange,
  placeholder = 'Search...',
  debounceDelay = 300,
  className = '',
  searchBy = '',
  disabled = false,
  loading = false
}: SearchInputDebouncedProps) {
  const [internalValue, setInternalValue] = useState(externalValue);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    setInternalValue(externalValue);
  }, [externalValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (internalValue !== externalValue) {
        onChange(internalValue);
        setIsTyping(false);
      }
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [internalValue, debounceDelay]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    setIsTyping(true);
  }, []);

  const handleClear = useCallback(() => {
    setInternalValue('');
    onChange('');
    setIsTyping(false);
  }, [onChange]);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          value={internalValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full pl-10 pr-10 py-2.5
            border border-gray-300 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
            disabled:bg-gray-50 disabled:cursor-not-allowed
            text-sm sm:text-base
            transition-all duration-200
          `}
        />
        {(internalValue || loading || isTyping) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {(loading || isTyping) ? (
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            ) : internalValue ? (
              <button
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        )}
      </div>
      {searchBy && (
        <p className="mt-1 text-xs text-gray-500">
          Searching by: {searchBy}
        </p>
      )}
    </div>
  );
}